import {
  anchorPosition,
  boundsOfValue,
  boundsOfValues,
  type Evaluated,
} from '@/domain/construction';
import { type Bounds } from '@/domain/geometry';
import { angle, type Vec2, vec } from '@/domain/math';
import { type EntityId, inDrawOrder } from '@/domain/model';
import { distanceToValue, type Scene } from '@/engine';

/**
 * Which way a point's name is written.
 *
 * ## The assumption this replaces
 *
 * Names used to be drawn up and to the right, always, on the reasoning that a textbook does
 * the same and that the corner above-right of a vertex is usually empty. It is a fair
 * description of the *average* vertex and wrong about most real ones. Four solids drawn from
 * the exercise book put `A` on top of edge AB, `B` across the lateral edge SB and `C` through
 * the base — every figure, the same defect, because the bottom-left vertex of a base has its
 * whole figure up and to the right of it.
 *
 * ## What a person does instead
 *
 * Writes the letter where no line goes. That is directly computable: collect the direction of
 * every drawn edge leaving the point, sort those directions by angle, and write the name in
 * the middle of the widest gap between consecutive ones. At the corner of a square that is the
 * diagonal pointing out of the corner; at a point midway along a segment it is a perpendicular;
 * at a free point with no edges at all it is the textbook corner, up and to the right.
 *
 * ## Why it lives in `rendering`
 *
 * It is a statement about legibility, not about geometry: the figure is equally correct with
 * the name in either place. Nothing in `domain` may depend on it, and it needs the evaluated
 * scene rather than the construction graph, since where an edge *goes* is a fact about its
 * value.
 */

/** Up and to the right, for a point with nothing attached to it. The old behaviour, kept. */
export const DEFAULT_NAME_DIRECTION: Vec2 = Object.freeze(vec.normalize(vec.vec2(1, 1)));

/**
 * A point counts as *on* an edge within this distance, in world units.
 *
 * Generous on purpose. The alternative is missing an edge that a rounding error moved off the
 * vertex by a millionth of a unit, and a missed edge is exactly the case this exists to fix.
 */
const TOUCHING = 1e-6;

/**
 * Directions along which some drawn edge leaves `at`.
 *
 * A segment contributes one direction when `at` is an endpoint and two when it lies in the
 * middle; a line or ray through the point contributes along its own direction. Curves are
 * ignored — a name beside a circle has no crowded corner to escape from, and the tangent at
 * the point would be a poor guide to where the circle actually is.
 */
function directionsAt(at: Vec2, value: Evaluated): readonly Vec2[] {
  const out: Vec2[] = [];

  const fromEndpoints = (a: Vec2, b: Vec2): void => {
    const atA = vec.distance(at, a) <= TOUCHING;
    const atB = vec.distance(at, b) <= TOUCHING;
    if (atA && !atB) out.push(vec.normalize(vec.subtract(b, a)));
    else if (atB && !atA) out.push(vec.normalize(vec.subtract(a, b)));
    else if (!atA && !atB && onSegmentBetween(at, a, b)) {
      out.push(vec.normalize(vec.subtract(a, at)));
      out.push(vec.normalize(vec.subtract(b, at)));
    }
  };

  switch (value.kind) {
    // A viewpoint is not on the page. Nothing to draw, and nothing to name.
    case 'space':
      break;

    case 'segment':
      fromEndpoints(value.segment.a, value.segment.b);
      break;
    case 'vector':
      fromEndpoints(value.from, value.to);
      break;
    case 'ray':
      if (vec.distance(at, value.ray.origin) <= TOUCHING)
        out.push(vec.normalize(value.ray.direction));
      break;
    case 'line': {
      const { origin, direction } = value.line;
      const along = vec.normalize(direction);
      // A line has no ends, so if it passes through the point it crowds both ways.
      const offset = vec.subtract(at, origin);
      const nearest = vec.add(origin, vec.scale(along, vec.dot(offset, along)));
      if (vec.distance(at, nearest) <= TOUCHING) {
        out.push(along);
        out.push(vec.negate(along));
      }
      break;
    }
    case 'polygon': {
      const { vertices } = value;
      for (let index = 0; index < vertices.length; index += 1) {
        const a = vertices[index];
        const b = vertices[(index + 1) % vertices.length];
        if (a !== undefined && b !== undefined) fromEndpoints(a, b);
      }
      break;
    }
    case 'polyline': {
      const { points, closed } = value;
      const last = closed ? points.length : points.length - 1;
      for (let index = 0; index < last; index += 1) {
        const a = points[index];
        const b = points[(index + 1) % points.length];
        if (a !== undefined && b !== undefined) fromEndpoints(a, b);
      }
      break;
    }
    /*
     * The two axes, as segments. A point on one of them is crowded along it exactly as it would
     * be by any other line, and the roots and intercepts a graph is marked at all sit on one.
     */
    case 'axes':
      fromEndpoints(value.xAxis.a, value.xAxis.b);
      fromEndpoints(value.yAxis.a, value.yAxis.b);
      break;

    /*
     * A curve *is* the crowding, when the point is on it.
     *
     * The comment below says curves have no crowded corner to escape from, and for a circle
     * that is true. It is false for the point a graph is marked at, which is the case this
     * whole branch exists for: `(1; -2)` at the minimum of `y = x³ − 3x` was printed straight
     * across the curve, because with nothing contributing a direction the search short-circuits
     * to the textbook corner and never measures anything.
     *
     * Contributed as the two ways the curve *leaves* the point rather than as its tangent. At a
     * turning point those both go the same way — up from a minimum, down from a maximum — and
     * the widest gap between them is the empty side, which is where a textbook writes it. A
     * tangent would have said "horizontal", leaving up and down equally good.
     */
    case 'function-graph': {
      for (const run of value.segments) {
        const near = nearestSample(at, run);
        if (near === null) continue;
        for (const neighbour of [run[near - 1], run[near + 1]]) {
          if (neighbour === undefined) continue;
          const away = vec.subtract(neighbour, at);
          if (vec.length(away) > TOUCHING) out.push(vec.normalize(away));
        }
      }
      break;
    }

    /*
     * Everything with no straight edge through the point, listed rather than defaulted so that
     * a new kind of value has to be considered here instead of silently contributing nothing.
     *
     * A name beside a circle sits outside it with plenty of room, and the tangent at the point
     * would say where the curve *goes* rather than where it *is* — a poor guide, because a
     * circle occupies the whole other side too.
     */
    case 'point':
    case 'circle':
    case 'arc':
    case 'ellipse':
    case 'solid':
    case 'image':
    case 'variation-table':
    case 'graph-marks':
    case 'text':
    case 'angle-mark':
    case 'measurement':
      break;
  }

  return out;
}

/**
 * Which sample of a plotted run the point sits at, or `null` if it is not on the run.
 *
 * "On" has to be loose here, and by a known amount. A marked point is computed exactly — the
 * minimum of `y = x³ − 3x` is at `(1, −2)` — while the curve beside it is a chain of chords
 * between samples, and a chord across a turning point passes *above* the turn. Demanding the
 * point lie on the ink to within a millionth would find the curve absent at precisely the
 * points that are marked.
 *
 * So the tolerance is one chord: as close to the drawn curve as the drawn curve is to itself.
 * A label on some unrelated point that happens to fall that near is then also treated as
 * crowded by the curve, which is not a concession — at that distance it genuinely is.
 */
function nearestSample(at: Vec2, run: readonly Vec2[]): number | null {
  let best: number | null = null;
  let least = Infinity;

  for (let index = 0; index < run.length; index += 1) {
    const sample = run[index];
    if (sample === undefined) continue;
    const distance = vec.distance(at, sample);
    if (distance < least) {
      least = distance;
      best = index;
    }
  }

  if (best === null) return null;

  const before = run[Math.max(0, best - 1)];
  const after = run[Math.min(run.length - 1, best + 1)];
  const chord = before === undefined || after === undefined ? 0 : vec.distance(before, after) / 2;
  return least <= Math.max(chord, TOUCHING) ? best : null;
}

function onSegmentBetween(at: Vec2, a: Vec2, b: Vec2): boolean {
  const span = vec.subtract(b, a);
  const length = vec.length(span);
  if (length <= TOUCHING) return false;
  const along = vec.scale(span, 1 / length);
  const travelled = vec.dot(vec.subtract(at, a), along);
  if (travelled <= TOUCHING || travelled >= length - TOUCHING) return false;
  return vec.distance(at, vec.add(a, vec.scale(along, travelled))) <= TOUCHING;
}

/**
 * Where to write each labelled object's name, as a unit vector in **world** space.
 *
 * World rather than screen because the caller knows how its own axes run: the canvas and the
 * exports disagree about which way `y` grows, and a direction that silently means "up" in one
 * and "down" in the other is the kind of bug that only shows up in a printed worksheet.
 *
 * Only labelled entities are computed, because only they are drawn — a document where every
 * point is named is the expensive case, and it is also the one where this matters most.
 */
export function nameDirections(
  scene: Scene,
  /**
   * How many probe radii out a label looks for ink, as a multiple. Four is the proved bound; the
   * only other value worth passing is `Infinity`, which consults the whole drawing and is what
   * this function used to do — the test uses it to assert the two agree.
   */
  reachFactor = 4
): ReadonlyMap<EntityId, Vec2> {
  const drawn: Evaluated[] = [];
  const named: { readonly id: EntityId; readonly at: Vec2 }[] = [];

  for (const entity of inDrawOrder(scene.document)) {
    if (!entity.visible) continue;
    const value = scene.values.get(entity.id);
    if (value === null || value === undefined) continue;
    drawn.push(value);
    if (entity.label === undefined) continue;
    const at = anchorPosition(value);
    if (at !== null) named.push({ id: entity.id, at });
  }

  /*
   * How far out to look for ink, in world units.
   *
   * A fraction of the drawing's own diagonal rather than a fixed number, because the only
   * scale-free statement available here is "about as far from the point as a letter is
   * wide". Labels are drawn at a constant *screen* size and an export fits the figure to the
   * page, so a letter occupies roughly the same fraction of any finished figure — which makes
   * this proportional guess a good deal better than a constant in world units, where it would
   * mean one thing at a=100 and something absurd at a=3.
   */
  const box = boundsOfValues(drawn);
  const probe =
    box === null ? 0 : Math.hypot(box.maxX - box.minX, box.maxY - box.minY) * PROBE_FRACTION;

  /*
   * The drawing's outline, for keeping names off the body of the figure.
   *
   * A vertex label belongs *outside* the object it names. Inside, it reads as annotating the
   * interior — a mark in the middle of a face rather than the corner it is attached to — and on
   * a solid it competes with every line crossing there. `A'` on the triangular prism landed
   * inside the top face for exactly this reason: the widest empty wedge at that corner pointed
   * into the solid, and nothing in the rule knew the solid was there.
   *
   * The convex hull is the right outline even for a figure that is not convex, because it is
   * never *too small*: a direction it calls outward really is clear of the drawing. The cost is
   * that a dent in the silhouette goes unnoticed, which loses an opportunity rather than causing
   * a mistake.
   */
  const outline = convexHull(drawn.flatMap(outlinePoints));

  /*
   * Each label consults the ink **near it**, not the whole drawing.
   *
   * ## Why this is exact and not an approximation
   *
   * Two questions are asked of `drawn`, and both have a reach.
   *
   * `directionsAt` only answers when the anchor **touches** the value — within `TOUCHING`, which
   * is 1e-6 — so anything further away contributes no candidate direction at all.
   *
   * `clearanceAt` measures from a probe point `probe` away from the anchor. A value with a point
   * within `2·probe` of the anchor is within `3·probe` of that probe point; a value further than
   * `4·probe` from the anchor is more than `3·probe` from it. So once any ink is inside `2·probe`,
   * nothing beyond `4·probe` can ever be the nearest — it is excluded by the triangle inequality,
   * not by a guess. When nothing at all is near, the search widens to the whole drawing, which is
   * the isolated-label case and is what used to happen every time.
   *
   * ## Why it was worth doing
   *
   * The inner loop ran once per label **per candidate direction**, and each run walked every
   * value in the document. Measured before: 20.09 ms per frame on 48 solids — and this pass runs
   * on every frame, so it was the ceiling on how large a figure could be dragged smoothly, now
   * that `coveredSegments` no longer is.
   */
  const boxes = drawn.map((value) => boundsOfValue(value));
  const reach = Math.max(probe * reachFactor, TOUCHING);

  /** How far a point is from a box, zero when inside it. */
  const gap = (box: Bounds, at: Vec2): number =>
    Math.hypot(
      Math.max(box.minX - at.x, 0, at.x - box.maxX),
      Math.max(box.minY - at.y, 0, at.y - box.maxY)
    );

  const result = new Map<EntityId, Vec2>();
  for (const { id, at } of named) {
    const near: Evaluated[] = [];
    for (const [index, value] of drawn.entries()) {
      const box = boxes[index];
      // An unbounded value — a line, an axis — reaches everywhere and is always consulted.
      if (box === null || box === undefined || gap(box, at) <= reach) near.push(value);
    }
    // Nothing anywhere near: fall back to the whole drawing, which is what always used to happen.
    const consulted = near.length === 0 ? drawn : near;

    const directions: Vec2[] = [];
    for (const value of consulted) directions.push(...directionsAt(at, value));
    result.set(
      id,
      awayFrom(directions, (candidate) => ({
        clearance: clearanceAt(at, candidate, probe, consulted),
        outward: !inside(vec.addScaled(at, candidate, probe), outline),
      }))
    );
  }

  return result;
}

/** The points a value contributes to the drawing's outline. */
function outlinePoints(value: Evaluated): readonly Vec2[] {
  // A viewpoint contributes nothing to the drawing's outline, having none.
  switch (value.kind) {
    case 'space':
      return [];

    case 'point':
      return [value.point];
    case 'segment':
      return [value.segment.a, value.segment.b];
    case 'vector':
      return [value.from, value.to];
    case 'polygon':
      return value.vertices;
    case 'polyline':
      return value.points;
    case 'circle':
      return boxCorners(value.circle.center, value.circle.radius, value.circle.radius);
    /*
     * The corners of the bounding box rather than the curve itself. A hull built from those
     * contains the box and therefore the conic, which is the safe direction to err in — it can
     * only make the outline larger, never leave part of the figure outside it.
     */
    case 'ellipse':
      return boxCorners(value.ellipse.center, value.ellipse.rx, value.ellipse.ry);
    /*
     * Everything unbounded, and everything whose extent is a matter of font metrics. A line has
     * no outline to be inside of, and a label is not part of the figure it annotates.
     */
    case 'line':
    case 'ray':
    case 'arc':
    case 'solid':
    case 'image':
    case 'function-graph':
    case 'axes':
    case 'variation-table':
    case 'graph-marks':
    case 'text':
    case 'angle-mark':
    case 'measurement':
      return [];
  }
}

function boxCorners(center: Vec2, rx: number, ry: number): readonly Vec2[] {
  return [
    vec.vec2(center.x - rx, center.y - ry),
    vec.vec2(center.x + rx, center.y - ry),
    vec.vec2(center.x + rx, center.y + ry),
    vec.vec2(center.x - rx, center.y + ry),
  ];
}

/** Andrew's monotone chain, counter-clockwise. Fewer than three points enclose nothing. */
function convexHull(points: readonly Vec2[]): readonly Vec2[] {
  if (points.length < 3) return [];

  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const turn = (o: Vec2, a: Vec2, b: Vec2): number =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const half = (order: readonly Vec2[]): Vec2[] => {
    const chain: Vec2[] = [];
    for (const point of order) {
      while (chain.length >= 2) {
        const last = chain[chain.length - 1];
        const previous = chain[chain.length - 2];
        if (last === undefined || previous === undefined || turn(previous, last, point) > 0) break;
        chain.pop();
      }
      chain.push(point);
    }
    chain.pop();
    return chain;
  };

  return [...half(sorted), ...half([...sorted].reverse())];
}

/**
 * Strictly inside the outline.
 *
 * A point *on* it counts as out, which is what every corner of the figure is — otherwise the
 * rule would report that no direction leaves a vertex, which is the opposite of the truth.
 */
function inside(point: Vec2, hull: readonly Vec2[]): boolean {
  if (hull.length < 3) return false;
  for (let index = 0; index < hull.length; index += 1) {
    const a = hull[index];
    const b = hull[(index + 1) % hull.length];
    if (a === undefined || b === undefined) continue;
    const side = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
    if (side <= 0) return false;
  }
  return true;
}

const PROBE_FRACTION = 0.045;

/**
 * How much empty space a name would have if written in `direction`.
 *
 * The wedge rule alone only knows about edges meeting the point, so it happily writes a name
 * across a line that merely *passes* nearby — `D` on the pyramid landed on the segment `AJ`,
 * which starts two vertices away and has nothing to do with `D`. Measuring the distance from
 * where the name would actually sit to the nearest ink catches exactly that, and it costs one
 * distance query per candidate rather than any new geometry.
 *
 * Points are skipped. A name belongs *to* a point and sits close to it by design, so counting
 * dots as obstacles would rule out every direction equally and decide nothing.
 */
function clearanceAt(
  at: Vec2,
  direction: Vec2,
  probe: number,
  drawn: readonly Evaluated[]
): number {
  if (probe <= 0) return Infinity;
  const where = vec.addScaled(at, direction, probe);

  let nearest = Infinity;
  for (const value of drawn) {
    if (value.kind === 'point') continue;
    const found = distanceToValue(value, where);
    if (found !== null && found.distance < nearest) nearest = found.distance;
  }
  return nearest;
}

/**
 * The direction that best avoids all of `directions`: the middle of the widest empty wedge.
 *
 * ## Why not the negated sum
 *
 * That was the first version and it is wrong in a way worth recording, because it looks right.
 * Adding unit vectors and negating puts the name opposite the crowd — but opposite directions
 * *cancel*, so a point in the middle of a segment contributes nothing to the sum at all. `J`,
 * the midpoint of `SD` with `AJ` and `CJ` drawn from it, came out with its name written along
 * `SD`: the two halves of the segment had annihilated each other and left "up" looking empty
 * when it was in fact occupied twice over.
 *
 * Sorting the angles and taking the largest gap has no such blind spot. It also stops caring
 * how *many* edges leave in some direction, which is right — three coincident edges crowd a
 * corner exactly as much as one does, and a sum would have weighted them triple.
 *
 * ## Ranking the gaps
 *
 * The widest gap is the first guess, not the answer. A gap between two edges can still be
 * crossed by something that does not touch the point at all, so each candidate is measured
 * against the drawing by `clearance` and any candidate that lands on ink is passed over. Only
 * among the ones that are genuinely clear does width decide, and ties there go to the textbook
 * corner, so a symmetric figure still names its vertices the way a reader expects rather than
 * by whichever edge happened to sort first.
 *
 * If every direction is obstructed — a point buried inside a dense figure — the one with the
 * most room wins. There is no good answer in that case and picking the least bad one silently
 * is better than pretending the first is fine.
 *
 * ## Then: outside the figure
 *
 * A vertex name belongs outside the object it names. The widest gap at the corner of a solid
 * very often points *into* it — the top face of a prism is the emptiest region for some
 * distance and is exactly the wrong place for the name of its corner, because a letter sitting
 * on a face reads as marking a point of that face.
 *
 * So among the candidates that are clear of ink, the ones leading out of the drawing's outline
 * win, and width decides between those. A point genuinely enclosed — a midpoint inside a block
 * — has no outward candidate and falls back to the roomiest interior direction.
 */
function awayFrom(
  directions: readonly Vec2[],
  score: (direction: Vec2) => { readonly clearance: number; readonly outward: boolean }
): Vec2 {
  if (directions.length === 0) return DEFAULT_NAME_DIRECTION;

  const angles = directions.map((direction) => vec.angle(direction)).sort((a, b) => a - b);
  const preferred = vec.angle(DEFAULT_NAME_DIRECTION);

  const all = angles.flatMap((from, index) => {
    const to = angles[(index + 1) % angles.length];
    if (to === undefined) return [];
    // The last gap wraps past 2π, and a single direction leaves one gap of the whole circle.
    const span = angles.length === 1 ? TAU : angle.normalize(to - from);
    const middle = from + span / 2;
    const direction = vec.fromAngle(middle);
    return [
      {
        direction,
        span,
        ...score(direction),
        toPreferred: Math.abs(angle.normalizeSigned(middle - preferred)),
      },
    ];
  });

  const roomiest = Math.max(...all.map((candidate) => candidate.clearance));
  // "Clear enough" rather than "clearest": a name 40% of the way to the ink is legible, and
  // insisting on the maximum would trade a wide obvious wedge for a sliver that happens to be
  // a hair emptier.
  const clear = all.filter((candidate) => candidate.clearance >= roomiest * 0.4);
  const legible = clear.length === 0 ? all : clear;

  /*
   * Outward *among the legible ones*, not before them. Ordering it the other way round was the
   * first attempt and it broke a case that already worked: a point just inside the outline has
   * candidates that barely escape it, and one of those escapes happened to run straight along a
   * segment. Landing on ink is the worse failure of the two, so clearance filters first and
   * being outside the figure decides between the survivors.
   */
  const outward = legible.filter((candidate) => candidate.outward);
  const usable = outward.length > 0 ? outward : legible;

  let best = usable[0];
  if (best === undefined) return DEFAULT_NAME_DIRECTION;
  for (const candidate of usable) {
    if (
      candidate.span > best.span + 1e-9 ||
      (candidate.span > best.span - 1e-9 && candidate.toPreferred < best.toPreferred)
    ) {
      best = candidate;
    }
  }

  return best.direction;
}

const TAU = Math.PI * 2;
